import React, { useState, useEffect, useRef } from "react";
import styles from "./index.module.scss";
import Button from "~components/Button";
import SSOLogin from "../Header/utils";
import { useMediaQuery } from "~hooks/useMediaQuery";
import cn from "classnames";
import { SVG_LIBRARY, SvgAsset } from "~components/Svg";

const Header = (props) => {
  const [userInfo, setUserInfo] = useState(null);
  const ssoLogin = useRef(null);
  const desktopSDK = useRef(null);
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [menuState, setMenuState] = useState(false);
  const [accountMenuState, setAccountMenuState] = useState(false);
  const [childMenuStates, setChildMenuStates] = useState([]);
  const [showOverlay, setShowOverlay] = useState(false);
  const headerData = props.pageData?.header;
  const menuData = headerData?.menuItems;
  const [transparent, setTransparent] = useState(false);
  const [consultBtnToolTipState, setConsultBtnToolTipState] = useState(false);
  const hideOnvoLogo = props.pageData?.pageData?.hideOnvoLogo;
  const [domLoaded, setDomLoaded] = useState(false);
  let hostname, currPath, transparentPage;

  // turn this backon when the desktopSDK is ready
  const isDesktop = useMediaQuery();
  const isMobile = useMediaQuery("(max-width: 719px)");
  const isTablet = useMediaQuery("(min-width: 720px) and (max-width: 1023px)");

  const onLoginSuccess = (user) => {
    setUserInfo(user);
    props.onLogin && props.onLogin(user);
    props.onSDKLogin && props.onSDKLogin(user);
    // To handle the case when user visit force login page directly
    if (props.forceLogin) {
      window.location.reload();
    }
  };

  const onVisibilityChange = (visible) => {
    setAccountMenuState(false);
    props.onSDKVisibilityChange && props.onSDKVisibilityChange(visible);
  };

  useEffect(() => {
    if (!isDesktop || props.disableSSO) return;
    // Check if window.AlpsLoginSDK exists before loading
    let loginScript;
    if (!window.AlpsLoginSDK) {
      loginScript = document.createElement("script");
      loginScript.src = headerData.ssoSDK.desktop;
      loginScript.async = true;
      loginScript.onload = () => {
        if (typeof AlpsLoginSDK !== "undefined") {
          desktopSDK.current = new AlpsLoginSDK();
          desktopSDK.current.init({
            onLoginSuccess: onLoginSuccess,
            onVisibilityChange: onVisibilityChange,
          });
          window.alpsLoginSDKInstance = desktopSDK.current;
          setScriptLoaded(true);
          props.onSDKInitialized && props.onSDKInitialized();
        } else {
          console.error("AlpsLoginSDK is not defined.");
        }
      };
      loginScript.onerror = () => {
        console.error("AlpsLoginSDK failed to load.");
      };
      if (loginScript && document?.head) {
        document.head.appendChild(loginScript);
      }
    }

    return () => {
      if (loginScript && document?.head) {
        document.head.removeChild(loginScript);
      }
    };
  }, [isDesktop]);

  useEffect(() => {
    ssoLogin.current = new SSOLogin(props);
    ssoLogin.current.checkUserInfo();
    setUserInfo(ssoLogin.current.getUserInfo());
    // DEBUG ONLY!
    // setUserInfo({ temp: true })
  }, [scriptLoaded]);

  useEffect(() => {
    let aMenuChildren = [];
    menuData?.map(() => {
      aMenuChildren.push(false);
    });
    setChildMenuStates(aMenuChildren);
    // check transparency rules
    hostname = window.location.hostname;
    currPath = window.location.pathname;
    if (
      headerData?.transparent?.origins?.includes(hostname) &&
      headerData?.transparent?.paths?.includes(currPath)
    ) {
      transparentPage = true;
      setTransparent(true);
    }
    window.addEventListener("scroll", handleHeaderScroll);
    return () => {
      window.removeEventListener("scroll", handleHeaderScroll);
    };
  }, []);

  useEffect(() => {
    if (isMobile || isTablet) {
      setDomLoaded(true);
    } else if (isDesktop && scriptLoaded) {
      if (!domLoaded) {
        setDomLoaded(true);
      }
    }
  }, [scriptLoaded, isMobile, isTablet]);

  const handleHeaderScroll = () => {
    const fromTop = window.scrollY;
    if (transparentPage) {
      if (fromTop > 0) {
        setTransparent(false);
      } else {
        setTransparent(true);
      }
    }
  };

  const hamburgerMenuHandler = () => {
    let currentMenuState = !menuState ? true : false;
    setMenuState(currentMenuState);
    if (!currentMenuState) {
      setShowOverlay(false);
      let menuChildren = [...childMenuStates];
      menuChildren.fill(false);
      setChildMenuStates(menuChildren);
    }
  };

  const HamburgerMenu = ({ onClick = () => {} }) => {
    const onClickHandler = () => {
      onClick();
    };
    return (
      <div className={styles.alpsHeaderHamburger} onClick={onClickHandler}>
        <div className={styles.alpsHeaderHamburgerInner}>
          <i
            className={
              !isDesktop && menuState
                ? styles.alpsHeaderHamburgerCrossingIcon
                : styles.alpsHeaderHamburgerBars
            }
          />
        </div>
      </div>
    );
  };

  return (
    <div
      className={cn(
        styles.alpsHeaderOld,
        { [styles[`alpsHeaderTheme${headerData?.theme}`]]: headerData?.theme },
        { [styles.onvoHeaderTransparent]: transparent },
        { [styles.hidden]: !domLoaded }
      )}
      id="alpsHeaderId"
    >
      <div className={cn(styles.alpsHeaderContent)}>
        <div
          className={cn(
            styles.alpsHeaderContentInner,
            "grid-container-grid grid-row"
          )}
        >
          <HeaderLogo
            theme={headerData?.theme}
            link={headerData?.homeLink}
            title={headerData?.homeTitle}
            isDesktop={isDesktop}
          />
          <div
            className={styles.logoWhite}
            id="onvo_header_logo"
            onClick={() => {
              window.location.href = headerData?.homeLink || "/";
            }}
            style={{ opacity: hideOnvoLogo ? "0" : "1" }}
          >
            {SVG_LIBRARY["logoWhite"]}
          </div>
          {isDesktop && (
            <MenuBody
              menuData={menuData}
              menuState={menuState}
              headerData={headerData}
              accountMenuState={accountMenuState}
              setAccountMenuState={setAccountMenuState}
              childMenuStates={childMenuStates}
              setChildMenuStates={setChildMenuStates}
              isDesktop={isDesktop}
              userInfo={userInfo}
              disableSSO={props.disableSSO}
              ssoLogin={ssoLogin}
              desktopSDK={desktopSDK}
              setShowOverlay={setShowOverlay}
              showOverlay={showOverlay}
              setConsultBtnToolTipState={setConsultBtnToolTipState}
              isTablet={isTablet}
            />
          )}
          {headerData?.consultButton && (
            <ConsultButton
              buttonData={headerData.consultButton}
              isDesktop={isDesktop}
              tooltipState={consultBtnToolTipState}
              setTooltipState={setConsultBtnToolTipState}
              setShowOverlay={setShowOverlay}
            />
          )}
          {!isDesktop && headerData?.loginButton?.version !== "prelaunch" ? (
            <HamburgerMenu onClick={hamburgerMenuHandler} />
          ) : (
            <LogInAccountButtons
              className={styles.alpsHeaderAccountBtn}
              headerData={headerData}
              accountMenuState={accountMenuState}
              setAccountMenuState={setAccountMenuState}
              childMenuStates={childMenuStates}
              setChildMenuStates={setChildMenuStates}
              isDesktop={isDesktop}
              userInfo={userInfo}
              disableSSO={props.disableSSO}
              ssoLogin={ssoLogin}
              desktopSDK={desktopSDK}
              showOverlay={showOverlay}
              setShowOverlay={setShowOverlay}
              setConsultBtnToolTipState={setConsultBtnToolTipState}
            />
          )}
          <div
            id={"alpsHeaderOverlay"}
            className={cn(
              styles.alpsHeaderOverlay,
              showOverlay ? styles.active : ""
            )}
            onClick={() => {
              resetMenus(
                setAccountMenuState,
                childMenuStates,
                setChildMenuStates,
                setShowOverlay
              );
            }}
          />
        </div>
        {userInfo && (
          <AccountMenu
            headerData={headerData}
            accountMenuState={accountMenuState}
            setAccountMenuState={setAccountMenuState}
            childMenuStates={childMenuStates}
            setChildMenuStates={setChildMenuStates}
            setShowOverlay={setShowOverlay}
            showOverlay={showOverlay}
            isDesktop={isDesktop}
            ssoLogin={ssoLogin}
            setUserInfo={setUserInfo}
            setConsultBtnToolTipState={setConsultBtnToolTipState}
          />
        )}
      </div>
      {!isDesktop && (
        <MenuBody
          menuData={menuData}
          menuState={menuState}
          headerData={headerData}
          accountMenuState={accountMenuState}
          setAccountMenuState={setAccountMenuState}
          childMenuStates={childMenuStates}
          setChildMenuStates={setChildMenuStates}
          isDesktop={isDesktop}
          userInfo={userInfo}
          disableSSO={props.disableSSO}
          ssoLogin={ssoLogin}
          desktopSDK={desktopSDK}
          setShowOverlay={setShowOverlay}
          showOverlay={showOverlay}
          setConsultBtnToolTipState={setConsultBtnToolTipState}
          isTablet={isTablet}
        />
      )}
    </div>
  );
};

const HeaderLogo = ({ theme = "", link, title, isDesktop }) => {
  if (theme === "Dark") {
    if (isDesktop) {
      return (
        <a href={link} title={title} alt={title}>
          {SVG_LIBRARY["wordmark"]}
        </a>
      );
    } else {
      return null;
    }
  } else {
    return (
      <a href={link} title={title} alt={title}>
        {SVG_LIBRARY["logo"]}
      </a>
    );
  }
};

const MenuBody = ({
  menuData,
  menuState,
  isDesktop,
  headerData,
  accountMenuState,
  setAccountMenuState,
  childMenuStates,
  setChildMenuStates,
  showOverlay,
  setShowOverlay,
  setConsultBtnToolTipState,
  disableSSO,
  ssoLogin,
  desktopSDK,
  userInfo,
  isTablet,
}) => {
  const handleMenuItemClick = (
    e,
    href,
    hasChildren,
    elemIdx,
    isChild = false
  ) => {
    e.preventDefault();
    e.stopPropagation();
    if (hasChildren) {
      if (!isDesktop) {
        let menuChildren = [...childMenuStates];
        menuChildren[elemIdx] = !menuChildren[elemIdx];
        setChildMenuStates(menuChildren);
        if (isChild) {
          setShowOverlay(false);
        }
      }
    } else {
      window.location.href = href;
    }
  };

  const handleMenuItemHover = (e, href, hasChildren, elemIdx, action) => {
    e.preventDefault();
    e.stopPropagation();
    let menuChildren = [...childMenuStates];
    if (hasChildren && isDesktop) {
      if (action === "over") {
        if (!menuChildren[elemIdx]) {
          menuChildren[elemIdx] = true;
          setShowOverlay(true);
          setAccountMenuState(false);
        }
      } else {
        menuChildren.fill(false);
        setShowOverlay(false);
      }
    }
    if (!hasChildren && isDesktop) {
      menuChildren.fill(false);
      setShowOverlay(false);
    }
    setChildMenuStates(menuChildren);
  };

  return (
    <div
      id={"headerMenuBody"}
      className={cn(
        styles.headerMenu,
        styles.menuBody,
        menuState ? styles.active : "",
        headerData?.loginButton?.version
          ? styles[headerData?.loginButton?.version]
          : ""
      )}
      onMouseLeave={(e) => {
        handleMenuItemHover(e, "", true, 0, "out");
      }}
    >
      <div className={cn(styles.menuBodyInner)}>
        <div
          className={cn(
            styles.menuBodyParent,
            childMenuStates.some((state) => state === true) ? styles.active : ""
          )}
        >
          <div
            className={cn(
              !isDesktop ? "grid-container-grid grid-row" : "",
              styles.menuBodyItems
            )}
          >
            {menuData?.map((menuItem, index) => {
              return (
                <a
                  key={index}
                  alt={menuItem.label}
                  className={cn(
                    styles.headerMenuLink,
                    menuItem.children ? styles.parent : "",
                    childMenuStates[index] ? styles.active : ""
                  )}
                  onClick={(e) => {
                    handleMenuItemClick(
                      e,
                      menuItem.href,
                      menuItem.children ? true : false,
                      index
                    );
                  }}
                  onMouseOver={(e) => {
                    handleMenuItemHover(
                      e,
                      "",
                      menuItem.children ? true : false,
                      index,
                      "over"
                    );
                  }}
                >
                  <span dangerouslySetInnerHTML={{ __html: menuItem.label }} />
                  <span
                    className={cn(
                      styles.headerMenuLinkArrow,
                      childMenuStates[index] ? styles.active : ""
                    )}
                    style={{
                      backgroundImage: `url(${headerData.backArrow})`,
                    }}
                  />
                </a>
              );
            })}
          </div>
          {!isDesktop && !isTablet && (
            <LogInAccountButtons
              className={styles.alpsHeaderAccountBtn}
              headerData={headerData}
              accountMenuState={accountMenuState}
              setAccountMenuState={setAccountMenuState}
              childMenuStates={childMenuStates}
              setChildMenuStates={setChildMenuStates}
              isDesktop={isDesktop}
              userInfo={userInfo}
              disableSSO={disableSSO}
              ssoLogin={ssoLogin}
              desktopSDK={desktopSDK}
              showOverlay={showOverlay}
              setShowOverlay={setShowOverlay}
              setConsultBtnToolTipState={setConsultBtnToolTipState}
            />
          )}
        </div>
        {menuData?.map((menuItem, index) => {
          if (menuItem.children) {
            return (
              <div
                key={index}
                className={cn(
                  styles.menuBodyChild,
                  `menuBodyChild${menuItem.id}-${index}`,
                  childMenuStates[index] ? styles.active : ""
                )}
              >
                <div
                  className={cn(
                    "grid-container-grid grid-row",
                    styles.menuBodyChildItemsWrapper
                  )}
                >
                  <div
                    className={cn(
                      styles.childMenuBackArrow,
                      "arrow",
                      headerData?.theme ? styles.childMenuBackArrowDark : ""
                    )}
                    onClick={(e) => {
                      handleMenuItemClick(
                        e,
                        "",
                        menuItem.children ? true : false,
                        index,
                        true
                      );
                    }}
                    style={{
                      backgroundImage: `url(${headerData.backArrow})`,
                    }}
                  />
                  <MenuBodyChild
                    menuItem={menuItem}
                    isDesktop={isDesktop}
                    headerData={headerData}
                  />
                </div>
                {!isDesktop && !isTablet && (
                  <LogInAccountButtons
                    className={styles.alpsHeaderAccountBtn}
                    headerData={headerData}
                    accountMenuState={accountMenuState}
                    setAccountMenuState={setAccountMenuState}
                    childMenuStates={childMenuStates}
                    setChildMenuStates={setChildMenuStates}
                    isDesktop={isDesktop}
                    userInfo={userInfo}
                    disableSSO={disableSSO}
                    ssoLogin={ssoLogin}
                    desktopSDK={desktopSDK}
                    showOverlay={showOverlay}
                    setShowOverlay={setShowOverlay}
                    setConsultBtnToolTipState={setConsultBtnToolTipState}
                  />
                )}
              </div>
            );
          }
        })}
      </div>
    </div>
  );
};

const MenuBodyChild = ({ menuItem, isDesktop, headerData }) => {
  return (
    <div className={styles.menuBodyChildItems}>
      {menuItem.children.map((childItem, index) => {
        if (isDesktop && childItem.type === "phone") {
          return;
        }
        return (
          <div
            key={index}
            className={cn(
              styles.menuBodyChildItem,
              headerData?.theme
                ? styles[`menuBodyChildItemTheme${headerData?.theme}`]
                : ""
            )}
          >
            {childItem.title &&
              (childItem.type === "socialMedia" && isDesktop ? (
                <></>
              ) : (
                <p
                  className={cn({
                    [styles[`menuBodyChildItemPTheme${headerData?.theme}`]]:
                      headerData?.theme,
                  })}
                  dangerouslySetInnerHTML={{ __html: childItem.title }}
                />
              ))}
            {childItem.button &&
              (!isDesktop ? (
                <Button
                  {...{
                    className: styles.childItemBtn,
                    label: childItem.button.label,
                    size: "b3",
                    variant: headerData?.theme ? "btnWhite" : "btnPrimary",
                    theme: "btnTransparent",
                    href: childItem.button.phone
                      ? `tel:${childItem.button.phone}`
                      : childItem.button.href,
                    icon: childItem.button.icon,
                  }}
                />
              ) : (
                <>
                  {childItem.button.icon && childItem.type === "weixin" && (
                    <SvgAsset
                      customClass={styles.childItemQr}
                      svgId={childItem.button.icon}
                    />
                  )}
                </>
              ))}
            {childItem.socialMedia && (
              <>
                {childItem.phTitle && isDesktop && (
                  <p dangerouslySetInnerHTML={{ __html: childItem.phTitle }} />
                )}
                {childItem.phNumber && isDesktop && (
                  <p
                    className={styles.childItemPhone}
                    dangerouslySetInnerHTML={{ __html: childItem.phNumber }}
                  />
                )}
                {childItem.title && isDesktop && (
                  <p dangerouslySetInnerHTML={{ __html: childItem.title }} />
                )}
                <div className={styles.childItemSocialItems}>
                  {childItem.socialMedia.map((smItem, index) => {
                    return (
                      <a
                        key={index}
                        className={cn(
                          styles.childItemSocialItem,
                          headerData?.theme
                            ? styles[
                                `childItemSocialItemTheme${headerData?.theme}`
                              ]
                            : ""
                        )}
                        href={smItem.href}
                        title={smItem.title}
                      >
                        {SVG_LIBRARY[smItem.icon]}
                      </a>
                    );
                  })}
                </div>
              </>
            )}
            {childItem.links && (
              <div className={styles.childItemLinks}>
                {childItem.links.map((link, index) => {
                  return (
                    <a
                      key={index}
                      className={cn(
                        styles.childItemLink,
                        headerData?.theme
                          ? styles[`childItemLinkTheme${headerData?.theme}`]
                          : ""
                      )}
                      href={link.href}
                      title={link.label}
                    >
                      <span dangerouslySetInnerHTML={{ __html: link.label }} />
                      <span
                        className={styles.childItemLinkArrow}
                        style={{
                          backgroundImage: `url(${headerData.rightArrow})`,
                        }}
                      />
                    </a>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const resetMenus = (
  setAccountMenuState,
  childMenuStates,
  setChildMenuStates,
  setShowOverlay
) => {
  setAccountMenuState(false);
  setChildMenuStates(childMenuStates.fill(false));
  setShowOverlay(false);
};

const loginHandler = (e, isDesktop, desktopSDK, ssoLogin) => {
  //handle web login
  if (isDesktop) {
    try {
      desktopSDK.current?.show();
    } catch (error) {
      console.error("AlpsLoginSDK has an Error in loginHandler", error);
    }
  } else {
    //handle mobile login
    if (e) {
      e.preventDefault();
    }
    if (ssoLogin) {
      ssoLogin.current.handleLogin();
    }
  }
};

const logoutHandler = async (
  ssoLogin,
  setUserInfo,
  isDesktop,
  setShowOverlay
) => {
  if (ssoLogin) {
    if (isDesktop && window.alpsLoginSDKInstance) {
      await new Promise((resolve) => {
        window.alpsLoginSDKInstance.logout(true).then((res) => {
          resolve(res.resultCode);
        });
      });
    }
    ssoLogin.current.handleLogout();
    setUserInfo(null);
    if (ssoLogin.current.props.forceLogin && isDesktop) {
      window.location.reload();
    }
  }
  setShowOverlay(false);
};

const LogInAccountButtons = ({
  className = "",
  accountMenuState,
  setAccountMenuState,
  childMenuStates,
  setChildMenuStates,
  showOverlay,
  setShowOverlay,
  setConsultBtnToolTipState,
  isDesktop,
  userInfo,
  headerData,
  disableSSO,
  ssoLogin,
  desktopSDK,
}) => {
  const btnLabel = userInfo
    ? headerData?.loginButton?.inLabel
    : headerData?.loginButton?.label;
  return (
    <>
      {disableSSO ? (
        <></>
      ) : (
        <Button
          id={"headerAccountButton"}
          {...{
            className: [
              styles.loginOutBtn,
              userInfo ? styles.logout : styles.login,
              className,
              { [styles.active]: accountMenuState && userInfo },
              {
                [styles[headerData?.loginButton?.version]]:
                  headerData?.loginButton?.version,
              },
            ],
            label: btnLabel,
            size: "b3",
            variant: "btnWhite",
            theme: "btnTransparent",
            onClick: (e) => {
              userInfo
                ? accountPopup(
                    e,
                    "click",
                    accountMenuState,
                    setAccountMenuState,
                    childMenuStates,
                    setChildMenuStates,
                    showOverlay,
                    setShowOverlay,
                    setConsultBtnToolTipState,
                    isDesktop
                  )
                : loginHandler(e, isDesktop, desktopSDK, ssoLogin);
            },
            icon: headerData?.loginButton?.icon,
            iconType: "embed",
            onMouseOver: isDesktop
              ? (e) => {
                  accountPopup(
                    e,
                    "mouseover",
                    accountMenuState,
                    setAccountMenuState,
                    childMenuStates,
                    setChildMenuStates,
                    showOverlay,
                    setShowOverlay,
                    setConsultBtnToolTipState,
                    isDesktop
                  );
                }
              : null,
          }}
        />
      )}
    </>
  );
};

const accountPopup = (
  e,
  type = "",
  accountMenuState,
  setAccountMenuState,
  childMenuStates,
  setChildMenuStates,
  showOverlay,
  setShowOverlay,
  setConsultBtnToolTipState,
  isDesktop
) => {
  if (e) {
    e.preventDefault();
  }
  if (type === "click") {
    setAccountMenuState(!accountMenuState);
    setConsultBtnToolTipState(false);
  } else if (type !== "") {
    if (type === "mouseover" && !accountMenuState) {
      setAccountMenuState(true);
      setConsultBtnToolTipState(false);
      if (isDesktop) {
        setChildMenuStates(childMenuStates.fill(false));
      }
    } else if (type === "mouseleave" && accountMenuState) {
      setAccountMenuState(false);
    }
  }
  if (!isDesktop) {
    setShowOverlay(!showOverlay);
  }
};

const AccountMenu = ({
  headerData = {},
  accountMenuState,
  setAccountMenuState,
  childMenuStates,
  setChildMenuStates,
  showOverlay,
  setShowOverlay,
  isDesktop,
  ssoLogin,
  setUserInfo,
  setConsultBtnToolTipState,
}) => {
  const handleAccountMenuClick = (e, href, id) => {
    if (id === "logout") {
      logoutHandler(ssoLogin, setUserInfo, isDesktop, setShowOverlay);
    } else {
      window.location.href = href;
    }
  };
  // render logged in account menu
  return (
    <div
      className={cn(styles.accountMenuWrapper, "grid-container-grid grid-row", {
        [styles.active]: accountMenuState,
      })}
      onMouseLeave={(e) => {
        accountPopup(
          e,
          "mouseleave",
          accountMenuState,
          setAccountMenuState,
          childMenuStates,
          setChildMenuStates,
          showOverlay,
          setShowOverlay,
          setConsultBtnToolTipState,
          isDesktop
        );
      }}
    >
      <div
        className={cn(
          styles.accountMenuBg,
          accountMenuState ? styles.active : ""
        )}
      >
        <div
          className={cn(
            styles.accountMenu,
            accountMenuState ? styles.active : ""
          )}
        >
          <i
            className={cn(
              styles.accountMenuClose,
              styles.alpsHeaderHamburgerCrossingIcon
            )}
            onClick={(e) => {
              accountPopup(
                e,
                "click",
                accountMenuState,
                setAccountMenuState,
                childMenuStates,
                setChildMenuStates,
                showOverlay,
                setShowOverlay,
                setConsultBtnToolTipState,
                isDesktop
              );
            }}
          />
          <div
            className={cn(
              styles.accountMenuItems,
              "grid-container-grid grid-row"
            )}
          >
            {headerData?.accountMenu?.links &&
              headerData?.accountMenu.links.map((link, index) => {
                return (
                  <div
                    key={index}
                    className={styles.accountMenuItem}
                    onClick={(e) => {
                      handleAccountMenuClick(e, link.href, link.id);
                    }}
                  >
                    {SVG_LIBRARY[link.icon]}
                    <a
                      className={styles.accountMenuItemLink}
                      dangerouslySetInnerHTML={{ __html: link.label }}
                    />
                  </div>
                );
              })}
          </div>
        </div>
      </div>
    </div>
  );
};

const ConsultButton = ({
  buttonData = {},
  isDesktop,
  tooltipState,
  setTooltipState,
  setShowOverlay,
}) => {
  const showConsultTooltip = (e) => {
    e.preventDefault();
    setShowOverlay(false);
    if (!tooltipState) {
      setTooltipState(true);
    }
  };

  const hideConsultTooltip = (e) => {
    e.preventDefault();
    setShowOverlay(false);
    if (tooltipState) {
      setTooltipState(false);
    }
  };

  return (
    <div
      className={cn(styles.consultButtonWrapper, {
        [styles.active]: tooltipState,
      })}
    >
      <Button
        {...{
          className: styles.consultButton,
          href: buttonData?.href,
          label: buttonData?.label,
          size: "b3",
          variant: "btnWhite",
          theme: "btnFill",
        }}
        onMouseOver={(e) => {
          isDesktop ? showConsultTooltip(e) : null;
        }}
        onClick={(e) => {
          !isDesktop ? showConsultTooltip(e) : null;
        }}
      />
      <div
        className={cn(styles.consultButtonTooltip, {
          [styles.active]: tooltipState,
        })}
        onMouseLeave={isDesktop ? hideConsultTooltip : null}
      >
        <div
          className={cn(styles.consultButtonTooltipContent, {
            [styles.active]: tooltipState,
          })}
        >
          {!isDesktop && (
            <span
              className={cn(
                styles.alpsHeaderHamburgerCrossingIcon,
                styles.consultButtonClose
              )}
              onClick={!isDesktop ? hideConsultTooltip : null}
            />
          )}
          {buttonData.title && (
            <p dangerouslySetInnerHTML={{ __html: buttonData.title }} />
          )}
          {buttonData.fellow && (
            <>
              <img
                className={cn(styles.consultButtonQr, "consultButtonQr")}
                src={buttonData.fellow.qrcode}
                alt={buttonData.fellow.name}
              />
              <div className={styles.consultButtonTooltipFellow}>
                <span className={styles.fellowPhoto}>
                  <img
                    src={buttonData.fellow.photo}
                    alt={buttonData.fellow.name}
                  />
                </span>
                <span className={styles.fellowInfo}>
                  <p
                    className={styles.fellowName}
                    dangerouslySetInnerHTML={{ __html: buttonData.fellow.name }}
                  />
                  {SVG_LIBRARY["onvoPill"]}
                  <p
                    className={styles.fellowTitle}
                    dangerouslySetInnerHTML={{
                      __html: buttonData.fellow.title,
                    }}
                  />
                </span>
                <span
                  className={styles.fellowMessage}
                  dangerouslySetInnerHTML={{
                    __html: buttonData.fellow.message,
                  }}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Header;
